import React, { useState } from "react";
import { Box, ClickAwayListener, Typography } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import useTheme from "../../../../common/hooks/useTheme";
import ExpandableListPopover, {
  PopoverGroup,
  PopoverItem,
} from "./ExpandableListPopover";
import { Theme } from "../../../../providers/ThemeProvider";

export type ReadOnlyFieldConfig = {
  label: string;
  value: string;
  fieldKey: string;
  items?: PopoverItem[];
  groups?: PopoverGroup[];
};

type Props = ReadOnlyFieldConfig & {
  openPopperId: string | null;
  setOpenPopperId: React.Dispatch<React.SetStateAction<string | null>>;
};

const ReadOnlyField = ({
  label,
  value,
  fieldKey,
  items,
  groups,
  openPopperId,
  setOpenPopperId,
}: Props) => {
  const theme: Theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);

  // Empty tiles show a dash/"No" and never open a popover.
  const expandable =
    (items?.some((item) => item.name !== "") ?? false) ||
    (groups?.some((group) => group.items.length > 0) ?? false);
  const open = expandable && openPopperId === fieldKey;

  const handleClick = () => {
    if (!expandable) return;
    setOpenPopperId(open ? null : fieldKey);
  };

  // Close on outside click. Functional update: if another tile's click
  // already claimed the popper, leave its id in place regardless of whether
  // the click-away fires before or after that click handler.
  const handleClickAway = () => {
    setOpenPopperId((current) => (current === fieldKey ? null : current));
  };

  const field = (
    <Box
      sx={styles.field(theme, open, expandable && isHovered, expandable)}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Box sx={styles.textColumn}>
        <Typography sx={styles.label(theme, open)}>{label}</Typography>
        <Typography sx={styles.value(theme)}>{value}</Typography>
      </Box>
      {expandable && (
        <Box sx={styles.chevron(theme, open)}>
          <KeyboardArrowDownIcon fontSize="small" />
        </Box>
      )}
      {expandable && (
        <ExpandableListPopover open={open} items={items} groups={groups} />
      )}
    </Box>
  );

  return open ? (
    <ClickAwayListener onClickAway={handleClickAway}>{field}</ClickAwayListener>
  ) : (
    field
  );
};

export default ReadOnlyField;

const styles = {
  field: (theme: Theme, open: boolean, hovered: boolean, expandable: boolean) => ({
    height: "56px",
    padding: "0 16px",
    borderRadius: "8px",
    border: `1px solid ${
      open
        ? theme.colors_ds.PRIMARY_TEXT_MAIN
        : theme.colors_ds.NEUTRAL_BORDER_WEAK
    }`,
    backgroundColor: hovered
      ? theme.colorTokens.N[20]
      : theme.colors_ds.NEUTRAL_BG_MAIN,
    display: "flex",
    flexDirection: "row" as "row",
    alignItems: "center",
    gap: "16px",
    cursor: expandable ? "pointer" : "default",
    position: "relative" as "relative",
  }),
  textColumn: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column" as "column",
    justifyContent: "center",
  },
  label: (theme: Theme, open: boolean) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "10px",
    lineHeight: "16px",
    color: open
      ? theme.colors_ds.PRIMARY_TEXT_MAIN
      : theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  value: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "19px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    whiteSpace: "nowrap" as "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
  chevron: (theme: Theme, open: boolean) => ({
    display: "flex",
    alignItems: "center",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    transition: "transform 0.3s ease",
    transform: open ? "rotate(180deg)" : "rotate(0deg)",
  }),
};
